import { useCallback } from 'react';
import { Highlight } from '../store/file';

export const useTextHighlight = () => {
  /**
   * @param content The text content of the current page
   * @param highlights The global highlights (with global head/tail)
   * @param pageOffset The global offset of the start of this page in the full text
   */
  const getHighlightedContent = useCallback(
    (content: string, highlights: Highlight[], pageOffset = 0) => {
      if (!highlights || highlights.length === 0) {
        return content;
      }

      const pageEnd = pageOffset + content.length;

      // Only consider highlights that overlap with this page
      const validHighlights = highlights
        .map((h) => {
          if (h.tail <= pageOffset || h.head >= pageEnd) return null;
          // Clamp highlight to page boundaries
          const mapped = {
            ...h,
            head: Math.max(h.head, pageOffset) - pageOffset,
            tail: Math.min(h.tail, pageEnd) - pageOffset,
          };
          // Additional validation: check if the mapped positions make sense
          if (mapped.head >= 0 && mapped.tail <= content.length) {
            const mappedText = content.substring(mapped.head, mapped.tail);

            // If the mapped text doesn't match the expected text, try to find it nearby
            if (mappedText !== h.txt) {
              console.warn('[Highlight] Text mismatch detected!', {
                expected: h.txt,
                actual: mappedText,
                globalPositions: [h.head, h.tail],
                pagePositions: [mapped.head, mapped.tail]
              });

              // Try to find the expected text in the page content
              const textIndex = content.indexOf(h.txt);
              if (textIndex !== -1) {
                mapped.head = textIndex;
                mapped.tail = textIndex + h.txt.length;
              } else {
                // If we can't find the exact text, try to find a similar text
                console.warn('[Highlight] Could not find exact text match, skipping highlight');
                return null;
              }
            }
          }

          return mapped;
        })
        .filter((h): h is Highlight => h !== null && h.head < h.tail && h.head < content.length && h.tail <= content.length);

      if (validHighlights.length === 0) {
        return content;
      }

      const sortedHighlights = [...validHighlights].sort((a, b) => a.head - b.head);

      let lastIndex = 0;
      const parts = [];

      sortedHighlights.forEach((highlight) => {
        if (highlight.head > lastIndex) {
          parts.push(content.substring(lastIndex, highlight.head));
        }
        const highlightedText = content.substring(highlight.head, highlight.tail);
        parts.push(
          `<mark style="background-color: ${highlight.color};">${highlightedText}</mark>`
        );
        lastIndex = highlight.tail;
      });

      if (lastIndex < content.length) {
        parts.push(content.substring(lastIndex));
      }

      return parts.join('');
    },
    []
  );

  return {
    getHighlightedContent,
  };
};
